import React from 'react';
import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const milestones = [
  { label: 'Creators served', value: '100k+' },
  { label: 'Clips generated', value: '4.2M' },
  { label: 'Export templates', value: '30+' },
  { label: 'Processing time saved', value: '18 years' },
];

const values = [
  {
    title: 'Creator-First',
    description: 'Every feature ships from real creator interviews and late-night Twitch streams. If it doesn’t make publishing easier, we don’t build it.',
  },
  {
    title: 'Speed with Quality',
    description: 'AI should feel like a senior editor—not a compromise. We combine automation with manual controls so you keep creative intent.',
  },
  {
    title: 'Transparency',
    description: 'Clear pricing, human support, and roadmap updates in public keep the community plugged into what’s next.',
  },
];

const About: React.FC = () => {
  return (
    <>
      <SEOHead
        title="About ClipThis"
        description="ClipThis is a creator-built AI editing platform designed to turn long footage into publish-ready clips in minutes."
        url="/about"
        schemaData={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'ClipThis',
          url: 'https://clipthis.io/about',
        }}
      />
      <BreadcrumbSchema
        items={[
          { name: 'About', path: '/about' },
        ]}
      />
      <div className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 dot-pattern-animated opacity-30 pointer-events-none" />
        <div className="relative z-10">
        <section className="max-w-5xl mx-auto px-6 py-16 animate-fade-up">
          <p className="text-red-600 uppercase tracking-[0.3em] text-xs mb-4">Our story</p>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">Built by a solo creator who edits daily</h1>
          <p className="text-lg text-gray-600 leading-relaxed mb-8">
            ClipThis started as a weekend hack to automatically find hype moments inside marathon Twitch streams.
            Today it powers highlight workflows for indie streamers, agencies, and media teams who need fast turnarounds without burning out.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {milestones.map((item) => (
              <div key={item.label} className="rounded-2xl border border-gray-200 p-4 text-center bg-gray-50">
                <div className="text-2xl font-bold text-gray-900">{item.value}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mt-1">{item.label}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gray-50 border-t border-b border-gray-100 animate-fade-up">
          <div className="max-w-5xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-10">
            <div>
              <p className="text-red-600 uppercase tracking-[0.3em] text-xs mb-2">Mission</p>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Create more without hiring a post-production team</h2>
              <p className="text-gray-600 leading-relaxed">
                AI shouldn’t replace your voice—it should make it easier to publish consistently. ClipThis handles the boring parts:
                finding highlights, reformatting for every platform, and tracking performance.
              </p>
            </div>
            <div className="space-y-6">
              {values.map((value) => (
                <div key={value.title} className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 py-16 animate-fade-up">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 rounded-2xl border border-gray-200 bg-white shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Solo founder, community roadmap</h3>
              <p className="text-gray-600 mb-4">
                Hi, I’m Ben—a full-time streamer-turned-builder. I still publish content weekly, which means every release is road-tested in a real workflow.
              </p>
              <p className="text-gray-600">
                Feature requests and bug reports all route through a public changelog and Discord. When you ask for something, you’ll see it move.
              </p>
            </div>
            <div className="p-6 rounded-2xl border border-gray-200 bg-white shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Want to collaborate?</h3>
              <p className="text-gray-600 mb-4">
                Agencies, game studios, and creator collectives partner with ClipThis for private templates and analytics dashboards.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• Managed onboarding for teams of 10+</li>
                <li>• Custom AI models for brand-safe detection</li>
                <li>• Dedicated success channel with 24h response times</li>
              </ul>
            </div>
          </div>
        </section>
        </div>
      </div>
    </>
  );
};

export default About;

